import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ClientChore, ClientChoreChecklistStatus } from '../models/client-chore.model';
import { User } from '../models/user.model';
import { ApiService } from './api.service';
import { PagedResults } from '../models/paged-results/paged-results.model';
import { LimitOffsetPagingMeta } from '../models/paged-results/limit-offset-paging.model';
import { AvailableClientChore } from '../models/available-client-chore.model';
import { ClientFileQualityChecklist } from '../models/client-file-quality-checklist.model';

export class PagedClientChores implements PagedResults {
  clientChores: ClientChore[];
  meta: LimitOffsetPagingMeta;

  get records() {
    return this.clientChores;
  }
}

@Injectable()
export class ClientChoresService extends ApiService {
  private http = inject(HttpClient);

  static readonly directUploadPath = "/api/v1/client_chore_direct_uploads";

  get(id: number): Observable<ClientChore> {
    return this.http.get<ClientChore>(`${environment.captureApi.url}/client_chores/${id}`);
  }

  getChecklist(id: number): Observable<ClientFileQualityChecklist> {
    return this.http.get<ClientFileQualityChecklist>(`${environment.captureApi.url}/client_chores/${id}/checklist`);
  }

  setChecklistStatus(id: number, status: ClientChoreChecklistStatus): Observable<ClientChore> {
    return this.http.patch<ClientChore>(
      `${environment.captureApi.url}/client_chores/${id}/checklist_status`,
      { status }
    );
  }

  getList(
    page: number,
    pageSize: number | 'all',
    statuses: Array<string> = ['new'],
    clientIds: Array<string> = [],
    userIds: Array<string> = [],
    names: Array<string> = [],
    inactive: boolean = false,
    requiresReview: boolean = false,
    excludedNames: Array<string> = [],
  ): Observable<PagedClientChores> {
    const searchParams = new URLSearchParams(`page=${page}&per_page=${pageSize}`);

    statuses.forEach(status => searchParams.append('statuses[]', status));
    clientIds.forEach(clientId => searchParams.append('client_ids[]', clientId));
    userIds.forEach(userId => searchParams.append('submitted_by_ids[]', userId));
    names.forEach(name => searchParams.append('names[]', name));
    excludedNames.forEach(name => searchParams.append('excluded_names[]', name));
    searchParams.append('inactive', inactive.toString());
    searchParams.append('requires_review', requiresReview.toString());
    return this.http.get<PagedClientChores>(`${environment.captureApi.url}/client_chores?${searchParams.toString()}`);
  }

  getMyQueue(
    page: number,
    pageSize: number,
    statuses: Array<string> = ['new'],
    clientIds: Array<string> = [],
    userIds: Array<string> = [],
    names: Array<string> = [],
    inactive: boolean = false,
    requiresReview: boolean = false,
  ): Observable<PagedClientChores> {
    // FIXME
    const searchParams = new URLSearchParams(`page=${page}&per_page=${pageSize}`);

    statuses.forEach(status => searchParams.append('statuses[]', status));
    clientIds.forEach(clientId => searchParams.append('client_ids[]', clientId));
    userIds.forEach(userId => searchParams.append('submitted_by_ids[]', userId));
    names.forEach(name => searchParams.append('names[]', name));
    searchParams.append('inactive', inactive.toString());
    searchParams.append('requires_review', requiresReview.toString());
    return this.http.get<PagedClientChores>(`${environment.captureApi.url}/client_chores/my_queue?${searchParams.toString()}`);
  }

  getMyQueueCount(): Observable<number> {
    return this.http.get<{ count: number }>(`${environment.captureApi.url}/client_chores/my_queue_count`)
      .pipe(map(resultsData => resultsData.count));
  }

  create(
    clientId: number,
    choreName: string,
    signedBlobId: string,
    params: any,
    notes: string
  ): Observable<ClientChore> {
    return this.http.post<ClientChore>(`${environment.captureApi.url}/client_chores`, {
      signed_id: signedBlobId,
      client_id: clientId,
      name: choreName,
      params,
      notes
    });
  }

  review(clientChoreId: number, reviewed: boolean): Observable<ClientChore> {
    return this.http.patch<ClientChore>(`${environment.captureApi.url}/client_chores/${clientChoreId}/review`, {
      reviewed
    });
  }

  fail(clientChoreId: number): Observable<ClientChore> {
    return this.http.patch<ClientChore>(`${environment.captureApi.url}/client_chores/${clientChoreId}/fail`, {});
  }

  restart(clientChoreId: number): Observable<ClientChore> {
    return this.http.patch<ClientChore>(`${environment.captureApi.url}/client_chores/${clientChoreId}/restart`, {});
  }

  getAvailableChores(): Observable<{ availableClientChores: AvailableClientChore[] }> {
    return this.http.get<{ availableClientChores: AvailableClientChore[] }>(
      `${environment.captureApi.url}/available_client_chores`
    );
  }

  getSubmittingUsers(): Observable<User[]> {
    return this.http.get<User[]>(
      `${environment.captureApi.url}/client_chores/submitting_users`
    );
  }

}
